import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';

const emptyForm = {
  school_id: '',
  parent_first_name: '',
  parent_last_name: '',
  parent_phone: '',
  parent_email: '',
  parent_password: '',
  student_first_name: '',
  student_last_name: '',
  standard: 'Demo',
  division: 'A',
  roll_number: '0',
  device_internal_id: ''
};

const MarketingDeviceAssign = ({ schools = [] }) => {
  const [form, setForm] = useState(emptyForm);
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError('');
    setSuccess(null);
  };

  const loadDevices = useCallback(async () => {
    try {
      setLoadingDevices(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const params = new URLSearchParams();
      if (form.school_id) params.set('school_id', form.school_id);

      const response = await fetch(
        `${API_BASE_URL}/admin/marketing-assign/available-devices?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to load available devices');
      }
      const data = await response.json();
      setDevices(Array.isArray(data.items) ? data.items : []);
      setForm((prev) => {
        if (!prev.device_internal_id) return prev;
        const stillThere = (data.items || []).some((d) => d.id === prev.device_internal_id);
        return stillThere ? prev : { ...prev, device_internal_id: '' };
      });
    } catch (err) {
      setDevices([]);
      setError(err.message || 'Failed to load available devices');
    } finally {
      setLoadingDevices(false);
    }
  }, [form.school_id]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      setError('');
      setSuccess(null);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/admin/marketing-assign`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...form,
          school_id: form.school_id ? Number(form.school_id) : null,
          roll_number: Number(form.roll_number) || 0
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to assign device');
      }

      setSuccess(data);
      setForm((prev) => ({
        ...emptyForm,
        school_id: prev.school_id,
        standard: prev.standard,
        division: prev.division
      }));
      await loadDevices();
    } catch (err) {
      setError(err.message || 'Failed to assign device');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Assign Test Device (Marketing / Sales)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create a parent login for the salesman, add a student under that login, and assign a free device — all in one submit.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            <Typography variant="subtitle2">{success.message}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Parent login phone: <strong>{success.parent?.phone}</strong>
              {success.parent?.password ? (
                <> · password: <strong>{success.parent.password}</strong></>
              ) : null}
            </Typography>
            <Typography variant="body2">
              Student: {success.student?.name} · Device: {success.device?.deviceId}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {success.loginHint}
            </Typography>
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>1. School</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>School</InputLabel>
                <Select
                  label="School"
                  value={form.school_id}
                  onChange={(e) => updateField('school_id', e.target.value)}
                >
                  {schools.map((school) => (
                    <MenuItem key={school.id} value={school.id}>{school.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>2. Salesman (Parent login)</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                required
                label="First name"
                value={form.parent_first_name}
                onChange={(e) => updateField('parent_first_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                required
                label="Last name"
                value={form.parent_last_name}
                onChange={(e) => updateField('parent_last_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                required
                label="Phone (login)"
                value={form.parent_phone}
                onChange={(e) => updateField('parent_phone', e.target.value)}
                helperText="Used for parent app login"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                required
                type="password"
                label="Password"
                value={form.parent_password}
                onChange={(e) => updateField('parent_password', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Email (optional)"
                value={form.parent_email}
                onChange={(e) => updateField('parent_email', e.target.value)}
                helperText="If blank, auto-generated from phone"
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>3. Student under this parent</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                required
                label="Student first name"
                value={form.student_first_name}
                onChange={(e) => updateField('student_first_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                required
                label="Student last name"
                value={form.student_last_name}
                onChange={(e) => updateField('student_last_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Standard"
                value={form.standard}
                onChange={(e) => updateField('standard', e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Division"
                value={form.division}
                onChange={(e) => updateField('division', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Roll no."
                value={form.roll_number}
                onChange={(e) => updateField('roll_number', e.target.value)}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>4. Device</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={8}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Select free device</InputLabel>
                <Select
                  label="Select free device"
                  value={form.device_internal_id}
                  onChange={(e) => updateField('device_internal_id', e.target.value)}
                  disabled={loadingDevices}
                >
                  {devices.length === 0 ? (
                    <MenuItem value="" disabled>
                      {loadingDevices ? 'Loading…' : 'No free devices available'}
                    </MenuItem>
                  ) : (
                    devices.map((device) => (
                      <MenuItem key={device.id} value={device.id}>
                        {device.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button variant="outlined" onClick={loadDevices} disabled={loadingDevices} fullWidth sx={{ height: 40 }}>
                {loadingDevices ? <CircularProgress size={18} /> : 'Refresh devices'}
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" disabled={submitting || loadingDevices}>
              {submitting ? 'Creating…' : 'Create parent + assign device'}
            </Button>
            <Button
              type="button"
              variant="text"
              disabled={submitting}
              onClick={() => {
                setForm(emptyForm);
                setError('');
                setSuccess(null);
              }}
            >
              Clear
            </Button>
          </Stack>
        </Box>
      </Card>
    </Box>
  );
};

export default MarketingDeviceAssign;
